import React, { useMemo } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import { Link } from '@this/shared/ui/navigations/link';
import type User from '@this/domain/user/user';
import type SearchResultItem from '@this/src/domain/search_result_item';
import CSSTransition from 'react-transition-group/CSSTransition';
import { Text } from '@this/components/shared/ui/data_displays/typography';
import TransitList from '../../../../domain/transit/transit_list';
import FlightList from '../../../../domain/flight/flight_list';
import TransportBoxShortDistance from '../transport_box/transport_box_short_distance';
import FlightBox from '../flight_box';
import HotelBox from '../hotel_box/HotelBox';
import { BoxButtonArea, BoxButton } from '../transport_box/right/right_in_outline.template';
import { BoxButtonText } from '../shared/box_parts';
import HotelList from '../../../../domain/hotel/hotel_list';
import type { Tab } from '../../../../domain/select_store';
import type SelectStore from '../../../../domain/select_store';
import HotelSelectedReason from './hotel_selected_reason';

type OutlineItemShortDistanceContainerProps = {
  sectionNumber: number;
  item: SearchResultItem;
  handleClickTab: (tab: Tab) => void;
  selected: boolean;
  hotelPriceLimit: number | null;
  store: SelectStore;
  isRequestForm: boolean;
  handleOpenShowSearchBox: () => void;
  isTripsForm?: boolean;
  handleCssTransitionExited: () => void;
  setItemLoading: (loading: boolean) => void;
};
const handleSearchAgain = (
  handleClickTab: (tab: Tab) => void,
  handleOpenShowSearchBox: () => void,
  selected: boolean,
  tab: Tab
) => {
  handleClickTab(selected ? 'none' : tab);
  handleOpenShowSearchBox();
};

const LoadingSpinner = ({ text }: { text?: string }) => (
  <>
    <img className="select__loading" src="/images/loading.gif" width={50} height={50} alt="" />
    {text && <div className="select__loading-text">{text}</div>}
  </>
);

const OutlineItemShortDistanceContainer: React.FC<OutlineItemShortDistanceContainerProps> = ({
  sectionNumber,
  item,
  handleClickTab,
  selected,
  hotelPriceLimit,
  handleCssTransitionExited,
  store,
  isRequestForm,
  handleOpenShowSearchBox,
  isTripsForm,
  setItemLoading
}) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const getNeedText = useMemo(
    () => (isNeed: boolean) => {
      const texts = isTripsForm ? ['削除する', '元に戻す'] : ['予約しない', '予約する'];
      return isNeed ? texts[0] : texts[1];
    },
    [isTripsForm]
  );

  const renderTransportSection = () => {
    return (
      <>
        <div style={styles.header}>
          <div style={styles.icon}>{sectionNumber}</div>
          <div style={styles.date}>{`交通機関：${item.queryItem.outdate}`}</div>
          {isTripsForm && item.isNeed && (
            <label style={styles.checkLabel} htmlFor={`transport_${item.index}`}>
              <input
                type="checkbox"
                id={`transport_${item.index}`}
                checked={!item.isNonOrderItem}
                onChange={() => item.toggleNonOrderItem()}
              />
              システムで手配
            </label>
          )}
          {item.type !== 'airPackage' && isRequestForm === false && (
            <Link style={styles.remove} onClick={item.toggleNeed}>
              {getNeedText(item.isNeed)}
            </Link>
          )}
        </div>
        <div style={styles.body} className="select-outline__item-block__body">
          {item.elementList == null || !(item.elementList instanceof TransitList) ? (
            <LoadingSpinner text="経路を検索中です" />
          ) : (
            (() => {
              const transitList = item.elementList;
              const currentTransit = transitList.current();

              // item.isNeedがfalseの場合は何も表示しない
              if (!item.isNeed) {
                return null;
              }

              // currentTransitがnullの場合
              if (currentTransit == null) {
                return (
                  <div className="select-pc__no-results">
                    <p>空席がありません</p>
                  </div>
                );
              }

              // loadingWithPair中の場合　　元実装まま。text入れていいんじゃ無いかというのと、触ってみる感じだとテキストの出ないSpinnerはでてなさそう。
              if (item.loadingWithPair()) {
                return item.selectedLoading ? <LoadingSpinner /> : <LoadingSpinner text="経路を検索中です" />;
              }

              // エラーがある場合
              if (item.error) {
                return (
                  <div style={styles.boxContent}>
                    <div>
                      <Text color="danger" style={{ whiteSpace: 'pre-line' }}>
                        {item.error}
                      </Text>
                    </div>
                    {!isRequestForm && (
                      <BoxButtonArea>
                        <BoxButton
                          selected={selected}
                          onClick={() =>
                            handleSearchAgain(handleClickTab, handleOpenShowSearchBox, selected, item.index)
                          }
                        >
                          <BoxButtonText>
                            {selected ? (
                              <>
                                再検索を
                                <br />
                                キャンセル
                              </>
                            ) : (
                              '再検索 >'
                            )}
                          </BoxButtonText>
                        </BoxButton>
                      </BoxButtonArea>
                    )}
                  </div>
                );
              }

              // データがない場合
              if (!item.elementList || item.elementList.list.length === 0) {
                return (
                  <div style={styles.boxContent}>
                    <div>
                      <p>予約可能な交通機関はありません</p>
                    </div>
                    {!isRequestForm && (
                      <BoxButtonArea>
                        <BoxButton
                          selected={selected}
                          onClick={() =>
                            handleSearchAgain(handleClickTab, handleOpenShowSearchBox, selected, item.index)
                          }
                        >
                          <BoxButtonText>
                            {selected ? (
                              <>
                                再検索を
                                <br />
                                キャンセル
                              </>
                            ) : (
                              '再検索 >'
                            )}
                          </BoxButtonText>
                        </BoxButton>
                      </BoxButtonArea>
                    )}
                  </div>
                );
              }

              // 正常なデータ表示
              return (
                <CSSTransition
                  in={transitList.showBox}
                  classNames="select-animation-box"
                  timeout={500}
                  onExited={handleCssTransitionExited}
                >
                  <TransportBoxShortDistance
                    transit={currentTransit}
                    inList={false}
                    handleClickTab={handleClickTab}
                    tab={item.index}
                    selected={selected}
                    domesticAirPriceIndex={transitList.domesticAirPriceIndex || 0}
                    store={store}
                    isRequestForm={isRequestForm}
                    parentLoading={setItemLoading}
                  />
                </CSSTransition>
              );
            })()
          )}
        </div>
      </>
    );
  };

  const renderFlightSection = () => {
    return (
      <>
        <div style={styles.header}>
          <div style={styles.icon}>{sectionNumber}</div>
          <div style={styles.date}>{`航空券：${item.queryItem.outdate}`}</div>
          {isTripsForm && item.isNeed && (
            <label style={styles.checkLabel} htmlFor={`flight_${item.index}`}>
              <input
                type="checkbox"
                id={`flight_${item.index}`}
                checked={!item.isNonOrderItem}
                onChange={() => item.toggleNonOrderItem()}
              />
              システムで手配
            </label>
          )}
          {isRequestForm === false && (
            <Link style={styles.remove} onClick={item.toggleNeed}>
              {getNeedText(item.isNeed)}
            </Link>
          )}
        </div>
        <div style={styles.body}>
          {item.isNeed &&
            (() => {
              const flightList = item.elementList as FlightList;

              // item.isNeedがfalseの場合は何も表示しない
              if (!item.isNeed) {
                return null;
              }

              // ローディング中の場合
              if (item.loading) {
                return item.selectedLoading ? <LoadingSpinner /> : <LoadingSpinner text="航空券を検索中です" />;
              }

              // エラーがある場合
              if (item.error) {
                return (
                  <div style={styles.boxContent}>
                    <div>
                      <Text color="danger" style={{ whiteSpace: 'pre-line' }}>
                        {item.error}
                      </Text>
                    </div>
                    {!isRequestForm && (
                      <BoxButtonArea>
                        <BoxButton
                          selected={selected}
                          onClick={() =>
                            handleSearchAgain(handleClickTab, handleOpenShowSearchBox, selected, item.index)
                          }
                        >
                          <BoxButtonText>
                            {selected ? (
                              <>
                                再検索を
                                <br />
                                キャンセル
                              </>
                            ) : (
                              '再検索 >'
                            )}
                          </BoxButtonText>
                        </BoxButton>
                      </BoxButtonArea>
                    )}
                  </div>
                );
              }
              // データがない場合
              if (!flightList || flightList.list.length === 0 || item.flightIndex == null) {
                return (
                  <div style={styles.boxContent}>
                    <div>
                      <p>予約可能な交通機関はありません</p>
                    </div>
                    {!isRequestForm && (
                      <BoxButtonArea>
                        <BoxButton
                          selected={selected}
                          onClick={() =>
                            handleSearchAgain(handleClickTab, handleOpenShowSearchBox, selected, item.index)
                          }
                        >
                          <BoxButtonText>
                            {selected ? (
                              <>
                                再検索を
                                <br />
                                キャンセル
                              </>
                            ) : (
                              '再検索 >'
                            )}
                          </BoxButtonText>
                        </BoxButton>
                      </BoxButtonArea>
                    )}
                  </div>
                );
              }

              // 正常なデータ表示
              return (
                <CSSTransition
                  in={flightList.showBox}
                  classNames="select-animation-box"
                  timeout={500}
                  onExited={handleCssTransitionExited}
                >
                  <FlightBox
                    flight={flightList.currentFlightCandidate(item.flightIndex)}
                    inList={false}
                    handleClickTab={handleClickTab}
                    tab={item.index}
                    flightIndex={item.flightIndex}
                    selected={selected}
                    isRequestForm={isRequestForm}
                    isDomestic={store.result.isDomesticAirResult()}
                  />
                </CSSTransition>
              );
            })()}
        </div>
      </>
    );
  };
  const renderHotelSection = () => {
    return (
      <>
        <div style={styles.header}>
          <div style={styles.icon}>{sectionNumber}</div>
          <div style={styles.date}>{`宿泊：${item.queryItem.outdate}〜${item.queryItem.homedate}`}</div>
          {isTripsForm && item.isNeed && (
            <label style={styles.checkLabel} htmlFor={`hotel_${item.index}`}>
              <input
                type="checkbox"
                id={`hotel_${item.index}`}
                checked={!item.isNonOrderItem}
                onChange={() => item.toggleNonOrderItem()}
              />
              システムで手配
            </label>
          )}
          {item.type !== 'airPackage' && isRequestForm === false && (
            <Link style={styles.remove} onClick={item.toggleNeed}>
              {getNeedText(item.isNeed)}
            </Link>
          )}
        </div>
        <div style={styles.body}>
          {(() => {
            const hotelList = item.elementList as HotelList;

            // item.isNeedがfalseの場合は何も表示しない
            if (!item.isNeed) {
              return null;
            }

            // ローディング中の場合
            if (item.loading) {
              return <LoadingSpinner text="ホテルを検索中です" />;
            }

            // エラーがある場合
            if (item.error) {
              return (
                <div style={styles.boxContent}>
                  <div>
                    <Text color="danger" style={{ whiteSpace: 'pre-line' }}>
                      {item.error}
                    </Text>
                  </div>
                  {!isRequestForm && (
                    <BoxButtonArea>
                      <BoxButton
                        selected={selected}
                        onClick={() =>
                          handleSearchAgain(handleClickTab, handleOpenShowSearchBox, selected, item.index)
                        }
                      >
                        <BoxButtonText>
                          {selected ? (
                            <>
                              再検索を
                              <br />
                              キャンセル
                            </>
                          ) : (
                            '再検索 >'
                          )}
                        </BoxButtonText>
                      </BoxButton>
                    </BoxButtonArea>
                  )}
                </div>
              );
            }
            // データがない場合
            if (!hotelList || hotelList.list.length === 0 || hotelList.list.every(h => !!h.sold_out)) {
              return (
                <div style={styles.boxContent}>
                  <div>
                    {item.filteredByOrganizationBreakfastFlag ? (
                      <div className="select-pc__no-results">
                        設定されている規程に基づき 朝食が許可されていないため該当のホテルを表示できません。
                        <br />
                        許可された条件で再度検索してください。
                      </div>
                    ) : (
                      <div className="select-pc__no-results">
                        ホテルが見つかりませんでした
                        <br />
                        検索条件を変更して再度検索してください。
                      </div>
                    )}
                  </div>
                  {!isRequestForm && (
                    <BoxButtonArea>
                      <BoxButton
                        selected={selected}
                        onClick={() =>
                          handleSearchAgain(handleClickTab, handleOpenShowSearchBox, selected, item.index)
                        }
                      >
                        <BoxButtonText>
                          {selected ? (
                            <>
                              再検索を
                              <br />
                              キャンセル
                            </>
                          ) : (
                            '再検索 >'
                          )}
                        </BoxButtonText>
                      </BoxButton>
                    </BoxButtonArea>
                  )}
                </div>
              );
            }

            // currentホテルがない場合
            if (!hotelList.current()) {
              return null;
            }

            // 正常なデータ表示
            return (
              <>
                <div className="select-pc__card-title">
                  <div className="bold" />
                </div>
                <div>
                  <CSSTransition
                    in={hotelList.showBox}
                    classNames="select-animation-box"
                    timeout={500}
                    onExited={handleCssTransitionExited}
                  >
                    <HotelBox
                      me={store.user as unknown as User}
                      destLocation={item.destLocation}
                      inList={false}
                      hotel={hotelList.current()}
                      hotels={hotelList}
                      isDomestic={item.isDomestic}
                      handleClickTab={handleClickTab}
                      hotelPriceLimit={hotelPriceLimit}
                      user={store.user}
                      tab={item.index}
                      selected={selected}
                      searchQueryId={store.result.searchQueryId}
                      isRequestForm={isRequestForm}
                    />
                  </CSSTransition>
                  <HotelSelectedReason
                    searchQueryItem={item.queryItem}
                    reasonStatus={item.reasonStatus}
                    selectedReason={item.selectedReason}
                    isFirst={hotelList.isFirst()}
                    selected={selected}
                  />
                </div>
              </>
            );
          })()}
        </div>
      </>
    );
  };
  const getSectionContent = () => {
    if (item.elementType() === 'transport' && (!item.elementList || item.elementList instanceof TransitList)) {
      return renderTransportSection();
    }
    if (item.elementType() === 'flight' && (!item.elementList || item.elementList instanceof FlightList)) {
      return renderFlightSection();
    }
    if (item.elementType() === 'hotel' && (!item.elementList || item.elementList instanceof HotelList)) {
      return renderHotelSection();
    }
    return null;
  };

  return <div className="select-outline__item-block">{getSectionContent()}</div>;
};

const getStyles = (theme: AITTheme) => ({
  header: {
    display: 'flex' as const
  },
  icon: {
    textAlign: 'center' as const,
    width: 20,
    height: 20,
    background: theme.iconColor,
    borderRadius: 10,
    color: 'white',
    fontWeight: 'bold' as const,
    marginRight: 10
  },
  date: {
    display: 'flex' as const,
    fontWeight: 'bold' as const,
    marginRight: 'auto'
  },
  checkLabel: {
    color: theme.linkColor,
    fontWeight: 'inherit' as const,
    cursor: 'pointer' as const
  },
  remove: {
    marginLeft: 20,
    color: theme.linkColor,
    textDecoration: 'none' as const,
    cursor: 'pointer' as const
  },
  body: {
    marginLeft: 8,
    borderLeft: `solid 4px ${theme.iconColor}`,
    padding: '10px 0 10px 10px'
  },
  boxContent: {
    color: theme.iconColor,
    margin: '20px 0px 20px 10px',
    display: 'flex' as const
  }
});

export default OutlineItemShortDistanceContainer;
